import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Eye,
  EyeOff,
  Star,
  Crown,
  Building2,
  Loader2,
  ArrowRight,
  Check,
  Zap,
  TrendingUp,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BusinessVisibility - /business/visibility
 * 
 * Shows:
 * - Current plan/tier + status
 * - Visibility controls (listing toggle, featured/sponsor status)
 * - Upgrade CTAs
 */
const BusinessVisibility = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!user.is_business) {
      toast.error("Business account required");
      navigate('/');
      return;
    }
    fetchVisibility();
  }, [user, navigate]);
  
  const fetchVisibility = async () => {
    try {
      const res = await fetch(`${API_URL}/api/business/dashboard/visibility`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to load visibility settings");
      }
    } catch (error) {
      toast.error("Failed to load visibility settings");
    } finally {
      setLoading(false);
    }
  };
  
  const updateVisibility = async (field, value) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/business/dashboard/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: value })
      });
      
      if (res.ok) {
        toast.success("Visibility updated");
        fetchVisibility();
      } else {
        throw new Error("Failed to update");
      }
    } catch (error) {
      toast.error("Failed to update visibility");
    } finally {
      setUpdating(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Alert variant="destructive">
          <AlertDescription>Unable to load visibility settings</AlertDescription>
        </Alert>
      </div>
    );
  }
  
  const getTierIcon = (tier) => {
    switch (tier) {
      case 'market_sponsor': return <Crown className="h-5 w-5 text-purple-500" />;
      case 'local_featured': return <Star className="h-5 w-5 text-yellow-500" />;
      default: return <Building2 className="h-5 w-5 text-gray-500" />;
    }
  };
  
  const getStatusBadge = (status, cancelAtPeriodEnd) => {
    if (cancelAtPeriodEnd) {
      return <Badge variant="warning" className="bg-orange-100 text-orange-700">Canceling</Badge>;
    }
    switch (status) {
      case 'active': return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'past_due': return <Badge className="bg-red-100 text-red-700">Past Due</Badge>;
      case 'canceled': return <Badge variant="secondary">Canceled</Badge>;
      default: return null;
    }
  };
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Settings className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Business Visibility</h1>
        </div>
        <p className="text-muted-foreground">
          Manage your business listing visibility and tier
        </p>
      </div>
      
      {/* Current Plan Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {getTierIcon(data.current_tier)}
              <div>
                <CardTitle className="text-xl">{data.tier_name}</CardTitle>
                <CardDescription>
                  {data.tier_price === 0 ? 'Free' : `$${data.tier_price}/month`}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(data.subscription_status, data.cancel_at_period_end)}
              <Link to="/business/billing">
                <Button variant="outline" size="sm">
                  Manage Billing
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.tier_features.map((feature, i) => (
              <Badge key={i} variant="secondary" className="flex items-center gap-1">
                <Check className="h-3 w-3" />
                {feature}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Visibility Controls */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Visibility Controls
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Listing Visibility Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-base">Directory Listing</Label>
              <p className="text-sm text-muted-foreground">
                Show your business in the public directory
              </p>
            </div>
            <Switch
              checked={data.visibility.listing_visible}
              onCheckedChange={(checked) => updateVisibility('listing_visible', checked)}
              disabled={updating}
            />
          </div>
          
          {/* Featured Status */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${data.visibility.is_featured ? 'bg-yellow-100' : 'bg-gray-100'}`}>
                <Star className={`h-5 w-5 ${data.visibility.is_featured ? 'text-yellow-500' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium">Featured Status</p>
                <p className="text-sm text-muted-foreground">
                  {data.visibility.is_featured 
                    ? 'Your business appears in featured sections'
                    : 'Not currently featured'}
                </p>
              </div>
            </div>
            {data.visibility.is_featured ? (
              <Badge className="bg-yellow-100 text-yellow-700">Active</Badge>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/subscription')}
              >
                Manage
              </Button>
            )}
          </div>
          
          {/* Sponsor Status */}
          <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${data.visibility.is_sponsor ? 'bg-purple-100' : 'bg-gray-100'}`}>
                <Crown className={`h-5 w-5 ${data.visibility.is_sponsor ? 'text-purple-500' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium">Market Sponsor Status</p>
                <p className="text-sm text-muted-foreground">
                  {data.visibility.is_sponsor 
                    ? 'Supporting groups across the market'
                    : 'Not currently a Market Sponsor'}
                </p>
              </div>
            </div>
            {data.visibility.is_sponsor ? (
              <Badge className="bg-purple-100 text-purple-700">Active</Badge>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/subscription')}
              >
                Manage
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Plan options (only show if not on highest tier) */}
      {data.upgrade_options && data.upgrade_options.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Available Plans</CardTitle>
            <CardDescription>Support groups and the broader ecosystem</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {data.upgrade_options.map((option) => (
                <div 
                  key={option.tier_id}
                  className={`p-4 rounded-lg border-2 ${
                    option.tier_id === 'local_featured' 
                      ? 'border-yellow-300 bg-yellow-50/50' 
                      : 'border-purple-300 bg-purple-50/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {option.tier_id === 'market_sponsor' 
                      ? <Crown className="h-5 w-5 text-purple-500" />
                      : <Star className="h-5 w-5 text-yellow-500" />
                    }
                    <h3 className="font-semibold">{option.name}</h3>
                  </div>
                  <p className="text-2xl font-bold mb-3">${option.price}<span className="text-sm font-normal text-muted-foreground">/month</span></p>
                  <ul className="space-y-1 mb-4">
                    {option.features.slice(0, 3).map((f, i) => (
                      <li key={i} className="text-sm flex items-center gap-1">
                        <Check className="h-3 w-3 text-green-500" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full"
                    variant={option.tier_id === 'market_sponsor' ? 'default' : 'outline'}
                    onClick={() => navigate('/subscription')}
                  >
                    Select plan
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BusinessVisibility;
